import React, { Component } from "react";
import PropTypes from "prop-types";
import { withStyles } from "material-ui/styles";
import IconButton from "material-ui/IconButton";
import DashboardIcon from "material-ui-icons/Dashboard";
import ShowChartIcon from "material-ui-icons/ShowChart";
import OvChartPanelContainer from "./OvChartPanelContainer";
import { blue, orange, green } from "material-ui/colors";
import { dollarValue } from "./helpers/presentation";
import { generateTimeSeries } from "./helpers/charts";
import dimensions from "react-dimensions";

import OvTimeBarMenu from "./OvTimeBarMenu";

import {
  XYPlot,
  XAxis,
  YAxis,
  HorizontalGridLines,
  VerticalBarSeries,
  DiscreteColorLegend,
  Crosshair
} from "react-vis";

import { timeBarExpandRange, formatTimeCrosshairTitle } from "./helpers/charts";

const styles = theme => ({
  root: {
    padding: 10,
    marginBottom: 20
  },
  dimButton: {
    color: theme.palette.action.disabled
  },
  xaxis: {
    height: 60,
    borderWidth: 1,
    borderColor: "red",
    borderStyle: "solid"
  },
  seriesLegend: {
    position: "absolute",
    top: 16,
    right: 110
  }
});

const generateSeries = (data, accumulate) => {
  const template = [
    {
      field: "instance_cost",
      title: "Instance cost",
      color: blue[500]
    },
    {
      field: "license_cost",
      title: "License cost",
      color: orange[500]
    },
    {
      field: "storage_cost",
      title: "Storage cost",
      color: green[500]
    }
  ];

  return generateTimeSeries(template, data, accumulate);
};

class OvTimeCostChart extends Component {
  constructor(props) {
    super(props);

    this.state = {
      crosshairValues: [],
      showInstanceCost: true,
      showLicenseCost: true,
      accumulate: false,
      stack: true,
      series: generateSeries(props.data, false),
      menuAnchor: null,
      menuOpen: false,
      menuPeriod: { start: null, end: null }
    };
    this.onLegendButtonClick = this.onLegendButtonClick.bind(this);
    this.onStackedButtonClick = this.onStackedButtonClick.bind(this);
    this.onAccumulateButtonClick = this.onAccumulateButtonClick.bind(this);
    this.handleNearestX = this.handleNearestX.bind(this);
    this.mouseLeaveHandler = this.mouseLeaveHandler.bind(this);
    this.formatCrosshairItems = this.formatCrosshairItems.bind(this);
    this.onBarClick = this.onBarClick.bind(this);
    this.handleRequestMenuClose = this.handleRequestMenuClose.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    this.setState({
      series: generateSeries(nextProps.data, this.state.accumulate)
    });
  }

  renderCumulativeState() {
    return `${this.state.accumulate ? "Cumulative" : "Raw"}`;
  }

  /* TODO: always show at least one series. Never show no series..*/
  onLegendButtonClick(item, i) {
    const { series } = this.state;
    series[i].disabled = !series[i].disabled;
    this.setState({ series });
  }

  onAccumulateButtonClick() {
    const newAccumulateState = !this.state.accumulate;
    this.setState({
      accumulate: newAccumulateState,
      series: generateSeries(this.props.data, newAccumulateState)
    });
  }

  onStackedButtonClick() {
    this.setState({ stack: !this.state.stack });
  }

  renderSeries() {
    const result = [];
    this.state.series.forEach((s, i) => {
      if (!s.disabled) {
        result.push(
          <VerticalBarSeries
            onNearestX={this.handleNearestX}
            color={s.color}
            key={i}
            data={this.state.series[i].data}
            onValueClick={this.onBarClick}
          />
        );
      }
    });
    return result;
  }

  handleNearestX(value, { index }) {
    const { series } = this.state;
    this.setState({
      crosshairValues: series.map(s => s.data[index])
    });
  }

  mouseLeaveHandler() {
    this.setState({ crosshairValues: [] });
  }

  formatCrosshairItems(values) {
    const { series } = this.state;
    return values.map((v, i) => {
      return {
        title: series[i].title,
        value: dollarValue(v.y)
      };
    });
  }

  renderPlot() {
    const { containerWidth, classes } = this.props;
    const plotWidth = containerWidth - 60;
    return (
      <XYPlot
        onMouseLeave={this.mouseLeaveHandler}
        margin={{ left: 80, bottom: 50 }}
        className={classes.plot}
        width={plotWidth}
        height={200}
        xType="time-utc"
        stackBy={this.state.stack ? "y" : null}
      >
        <XAxis tickLabelAngle={-30} />
        <YAxis tickFormat={v => `${dollarValue(v)}`} />
        <HorizontalGridLines />
        {this.renderSeries()}
        <Crosshair
          itemsFormat={this.formatCrosshairItems}
          titleFormat={formatTimeCrosshairTitle}
          values={this.state.crosshairValues}
        />
      </XYPlot>
    );
  }

  onBarClick(datapoint, event) {
    this.setState({
      menuOpen: true,
      menuAnchor: event.event.currentTarget,
      menuPeriod: timeBarExpandRange(datapoint.x, this.props.granularity)
    });
  }

  handleRequestMenuClose() {
    this.setState({ menuOpen: false });
  }

  render() {
    const { classes, loading } = this.props;
    return (
      <div>
        <OvChartPanelContainer
          chartTitle="Costs"
          chartSubheader={this.renderCumulativeState()}
          loading={loading}
          chart={this.renderPlot()}
          chartActions={
            <div>
              <DiscreteColorLegend
                className={classes.seriesLegend}
                onItemClick={this.onLegendButtonClick}
                orientation="horizontal"
                items={this.state.series}
              />
              <IconButton className={this.state.stack ? "" : classes.dimButton}>
                <DashboardIcon onClick={this.onStackedButtonClick} />
              </IconButton>
              <IconButton
                className={this.state.accumulate ? "" : classes.dimButton}
              >
                <ShowChartIcon onClick={this.onAccumulateButtonClick} />
              </IconButton>
            </div>
          }
        />

        <OvTimeBarMenu
          anchorEl={this.state.menuAnchor}
          open={this.state.menuOpen}
          filterState={this.props.filterState}
          onClose={this.handleRequestMenuClose}
          granularity={this.props.granularity}
          period={this.state.menuPeriod}
        />
      </div>
    );
  }
}

OvTimeCostChart.propTypes = {
  data: PropTypes.array.isRequired,
  granularity: PropTypes.object.isRequired,
  containerWidth: PropTypes.number.isRequired,
  filterState: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired
};

export { OvTimeCostChart };

export default withStyles(styles, { name: "OvTimeCostChart" })(
  dimensions({ elementResize: true })(OvTimeCostChart)
);



// WEBPACK FOOTER //
// ./src/OvTimeCostChart.js